import { formatDate } from '@angular/common';
import { Injectable, Injector } from '@angular/core';
import { UserService } from '@carm/_core/access-control-mgt/facade/user.service';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  IEmployeeDetails,
  IEmployeeDetailsResponse,
  IEmployeeListResponse,
  IRemoveEmployeeObject,
} from '../models';
import { IBaseResponse } from '@carm/_utils/models';
import { IProgramResponse } from '@carm/_core';
import { IGetRowsResult } from '@carm/shared/components/table/table-ag-grid/models/table-actions-model';
import { IEmployeeProgramsResponse } from '../models/manage-employees.model';
import { IVerificationPinResponse } from '../models/employee-list-response.model';
import { HttpParams } from '@angular/common/http';
import { LoaderSpinnerTypes } from '@carm/_core/models/loader-types.model';

@Injectable()
export class EmployeesService extends BaseApiService {
  constructor(protected injector: Injector, public userService: UserService) {
    super(injector);
  }

  public getEmployees(
    pageIndex: number,
    pageSize,
    sort,
    filterField: string = "",
    filterValue: string = ""
  ): Observable<IEmployeeListResponse> {
    const params = this.setParameters(
      pageIndex,
      pageSize,
      sort,
      filterField,
      filterValue
    );
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.business.default.baseUrl}/business/${businessUid}/employees`;
        return this.httpAuthGet<IEmployeeListResponse>(endpoint, params, LoaderSpinnerTypes.AgGrid).pipe(map(response => {
          return {
                  ... response,
                  totalRecords: response?.pagination?.totalCount,
                };
        }));
      })
    );
  }

  public getEmployeeDetails(uid: string): Observable<IEmployeeDetails> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.business.default.baseUrl}/business/${businessUid}/employees/find?fields=DEFAULT`;
        return this.httpAuthPost<IEmployeeDetailsResponse>(endpoint, {
          uid,
        }).pipe(
          map(data => {
            return this.mapEmployeeDetails(data);
          })
        );
      })
    );
  }

  public getCanExpireStaff(staffMemberUid: string): Observable<boolean> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.business.default.baseUrl}/business/${businessUid}/employees/can-expire`;
        return this.httpAuthPost<any>(endpoint, { staffMemberUid })
        .pipe(
          map(response => (response.responseStatus === 'SUCCESS')),
        );
      })
    );
  }

  public getEmployeePrograms(
    employeeUid: string,
    pageIndex: number,
    pageSize: number,
    sort: string,
    filterField: string,
    filterValue: string
  ): Observable<IGetRowsResult> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.business.default.baseUrl}/business/${businessUid}/employees/roles?fields=DEFAULT`;
        const params = {
          uid: employeeUid,
          filterField,
          filterValue,
          paginationParams: {
            currentPage: pageIndex,
            pageSize,
            sort,
          },
        };
        return this.httpAuthPost<IEmployeeProgramsResponse>(
          endpoint,
          params,
          null,
          LoaderSpinnerTypes.AgGrid
        ).pipe(
          map(data => {
            data.totalRecords = data.pagination?.totalCount;
            return data;
          })
        );
      })
    );
  }

  public mapEmployeeDetails(
    response: IEmployeeDetailsResponse
  ): IEmployeeDetails {
    let employeeDetails: IEmployeeDetails;
    if (response.uid) {
      employeeDetails = {
        name: response.name,
        isNativeBAM: response.isNativeBAM,
        phone: response.phone,
        email: response.email,
        lastModifier: response.lastModifyingUserFullName,
        lastModifiedDate: response.lastModifiedRoleTime
          ? formatDate(response.lastModifiedRoleTime, 'yyyy-MM-dd', 'en-CA')
          : null,
        uid: response.uid,
        groups: response.carmGroups,
        roles: response.roles
          ? response.roles.map(roleResp => ({
              ...roleResp,
              program: this.mapProgramResponse(roleResp.program),
            }))
          : [],
      };
    }

    return employeeDetails;
  }

  private mapProgramResponse(
    programResponse: IProgramResponse
  ): IProgramResponse {
    return { ...programResponse, name: programResponse.operatingName };
  }

  public removeEmployee(
    employeeRemoveObject: IRemoveEmployeeObject
  ): Observable<IBaseResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.business.default.baseUrl}/business/${businessUid}/employees/expire`;
        return this.httpAuthPost<IBaseResponse>(endpoint, employeeRemoveObject);
      })
    );
  }

  public listEmployeesForOrgUnit(
    orgUnitId: string,
    pageIndex: number,
    pageSize: number,
    sort: string,
    filterField: string = "",
    filterValue: string = ""
  ) : Observable<IEmployeeListResponse> {
    const params = this.setParameters(
      pageIndex,
      pageSize,
      sort,
      filterField,
      filterValue
    );
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.business.default.baseUrl}/business/${businessUid}/employees/orgunit/${orgUnitId}`;
        return this.httpAuthGet<IEmployeeListResponse>(endpoint, params, LoaderSpinnerTypes.AgGrid).pipe(map(response => {
          return {
                  ... response,
                  totalRecords: response?.pagination?.totalCount,
                };
        }));
      })
    );
  }

  public getVerificationPin() : Observable<IVerificationPinResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.business.default.baseUrl}/business/${businessUid}/employees/pin`;
        return this.httpAuthGet<IVerificationPinResponse>(endpoint);
      })
    );
  }

  public resetVerificationPin(pin: string): Observable<IBaseResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        // remove the hardcoded value below when security pin is reintroduced
        pin = '11111111';
        const endpoint = `${environment.business.default.baseUrl}/business/${businessUid}/employees/pin`;
        return this.httpAuthPost<IBaseResponse>(endpoint, {
          verificationPin: pin,
        });
      })
    );
  }

  private setParameters(
    pageIndex: number,
    pageSize: number,
    sort: string,
    filterField?: string,
    filterValue?: string
  ) {
    let params = new HttpParams()
      .set('currentPage', pageIndex  == undefined || pageIndex == 0 ? '1': pageIndex.toString() )
      .set('pageSize', pageSize ? pageSize.toString() : '100')
      .set('sort', sort)
      .set('fields', 'BASIC');

    if (filterField && filterValue) {
      params = params
        .set('filterField', filterField)
        .set('filterValue', filterValue);
    }
    return params;
  }
}
